import { cn } from '@/lib/utils';
import Mention from '@tiptap/extension-mention';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect } from 'react';
import {
  candidateNoteBaseExtensions,
  MENTION_HTML_ATTRIBUTES,
  mentionRenderHTML,
} from './candidate-note-extensions';

interface CandidateNoteBodyProps {
  body: string;
  className?: string;
}

export function CandidateNoteBody({ body, className }: CandidateNoteBodyProps) {
  const editor = useEditor({
    extensions: [
      // Headings 2-3 keep PeopleForce "Candidate Analysis" section structure
      // (h2 Summary / h3 Strengths / …); links open on click here since this is
      // a read-only surface. Read-only mention: render existing ones, no
      // @-suggestion dropdown.
      ...candidateNoteBaseExtensions({ openOnClick: true }),
      Mention.configure({
        HTMLAttributes: MENTION_HTML_ATTRIBUTES,
        renderHTML: mentionRenderHTML,
      }),
    ],
    content: body,
    editable: false,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm dark:prose-invert max-w-none break-words text-sm leading-relaxed text-foreground',
          '[&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1',
          // PF-synced h2/h3 should sit close to surrounding text — not the
          // default chunky prose spacing.
          '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold',
          '[&_h3]:mt-2 [&_h3]:mb-0.5 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wide [&_h3]:text-muted-foreground',
          '[&_p:has(>br.ProseMirror-trailingBreak:only-child)]:hidden',
          className
        ),
      },
    },
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== body) {
      editor.commands.setContent(body);
    }
  }, [editor, body]);

  return <EditorContent editor={editor} />;
}
